import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';

type CommentDoc = {
  _id: string;
  commentId: string;
  mediaId?: string;
  parentId?: string;
  text?: string;
  from?: { id?: string; username?: string };
  commentedAt?: string;
  replied?: boolean;
  reply?: string;
};

const Comments: React.FC = () => {
  // In dev: frontend runs on 3000, backend on 8080
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || (window.location.port === '3000' ? 'http://localhost:8080' : '')) as string;
  const { user } = useAuth();
  const noConnectedAccounts = !user?.facebookPageId && !user?.instagramBusinessId && !user?.whatsappBusinessAccountId && !user?.tiktokId;
  const [comments, setComments] = useState<CommentDoc[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState<any[]>([]);
  const [newRule, setNewRule] = useState<{ mediaId: string; keyword: string; replyText: string; mediaTitle?: string }>({ mediaId: '', keyword: '', replyText: '' });
  const [mediaOptions, setMediaOptions] = useState<Array<{ id: string; caption: string; media_url?: string; media_type?: string; timestamp?: string }>>([]);
  const [selectedMedia, setSelectedMedia] = useState<{ id: string; caption: string } | null>(null);

  const fetchComments = async (p = 1, s = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      const res = await fetch(`/api/comments?${params.toString()}`, { credentials: 'include' });
      const data = await res.json();
      setComments(data.comments || []);
      setPages(data.pagination?.pages || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComments(1, ''); }, []);

  const onSearch = () => fetchComments(1, search);
  const onPage = (_: any, value: number) => { setPage(value); fetchComments(value, search); };

  const sendReply = async (id: string) => {
    const reply = replyDrafts[id];
    if (!reply || !reply.trim()) return;
    const res = await fetch('/api/comments/reply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ commentId: id, reply })
    });
    if (res.ok) {
      setReplyDrafts(prev => ({ ...prev, [id]: '' }));
      // Refresh page
      fetchComments(page, search);
    }
  };

  const loadRules = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/comment-rules`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load rules');
      setRules(await res.json());
    } catch (e) {
      console.error('Rules load error:', e);
    }
  };
  const openRules = async () => { await loadRules(); setRulesOpen(true); };
  const closeRules = () => setRulesOpen(false);

  const saveRule = async () => {
    if (!newRule.mediaId || !newRule.keyword || !newRule.replyText) return;
    try {
      const res = await fetch(`${API_BASE}/api/comment-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newRule)
      });
      if (!res.ok) throw new Error('Failed to save rule');
      setNewRule({ mediaId: '', keyword: '', replyText: '' });
      await loadRules();
    } catch (e) {
      console.error('Save rule error:', e);
    }
  };

  // Load IG media when dialog opens
  useEffect(() => {
    if (!rulesOpen) return;
    (async () => {
      try {
        console.log('[IG MEDIA] fetch start ->', `${API_BASE}/api/instagram/media`);
        const res = await fetch(`${API_BASE}/api/instagram/media`, { credentials: 'include', cache: 'no-store' });
        console.log('[IG MEDIA] response', res.status, res.statusText);
        if (res.ok) {
          const data = await res.json();
          console.log('[IG MEDIA] data', data);
          setMediaOptions(data.media || []);
        } else {
          const txt = await res.text();
          console.error('[IG MEDIA] fetch failed', res.status, txt);
        }
      } catch (e) {
        console.error('Fetch IG media error:', e);
      }
    })();
  }, [rulesOpen]);
  const toggleRule = async (id: string, isActive: boolean) => {
    const rule = rules.find((r) => r._id === id);
    if (!rule) return;
    try {
      const res = await fetch(`${API_BASE}/api/comment-rules/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...rule, isActive: !isActive })
      });
      if (!res.ok) throw new Error('Failed to update rule');
      await loadRules();
    } catch (e) {
      console.error('Toggle rule error:', e);
    }
  };
  const deleteRule = async (id: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/comment-rules/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to delete rule');
      await loadRules();
    } catch (e) {
      console.error('Delete rule error:', e);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View and manage comments on your Instagram posts. Create automation rules to auto‑reply based on keywords,
        or send manual replies directly from here.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          placeholder="Search by text, username, media id"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={noConnectedAccounts}
          onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ width: 400 }}
        />
        <Button variant="contained" onClick={onSearch} disabled={noConnectedAccounts}>Search</Button>
        <Button variant="outlined" onClick={openRules} disabled={noConnectedAccounts}>Manage Rules</Button>
      </Box>

      {/* Main content blurred if no connected accounts */}
      <Box sx={{ ...(noConnectedAccounts ? { filter: 'blur(1.2px)', pointerEvents: 'none', userSelect: 'none' } : {}) }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>Media</TableCell>
              <TableCell>Response</TableCell>
              <TableCell align="right">Reply</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comments.map(c => (
              <TableRow key={c._id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.commentedAt ? new Date(c.commentedAt).toLocaleString() : '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.from?.username || c.from?.id || '-'}</TableCell>
                <TableCell>{c.text}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.mediaId || '-'}</TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  {c.reply ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {c.reply}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">-</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 280 }}>
                  {c.replied ? (
                    <Chip label="Replied" color="success" size="small" />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Write a reply..."
                        value={replyDrafts[c._id] || ''}
                        onChange={(e) => setReplyDrafts(prev => ({ ...prev, [c._id]: e.target.value }))}
                        sx={{ flex: 1 }}
                      />
                      <Button variant="contained" onClick={() => sendReply(c._id)}>Send</Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pages} page={page} onChange={onPage} color="primary" />
        </Box>
      )}

      {/* Rules Dialog */}
      <Dialog open={rulesOpen} onClose={closeRules} maxWidth="md" fullWidth>
        <DialogTitle>Comment Automation Rules</DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              px: { xs: 0, sm: 0 },
              py: 0.5
            }}>
              <Autocomplete
                size="small"
                options={mediaOptions}
                loading={!mediaOptions || mediaOptions.length === 0}
                noOptionsText={mediaOptions.length === 0 ? 'No posts found or permission missing' : 'No options'}
                getOptionLabel={(opt) => opt.caption?.substring(0, 80) || opt.id}
                onChange={(_, val) => {
                  setSelectedMedia(val as any);
                  setNewRule({ ...newRule, mediaId: (val as any)?.id || '', mediaTitle: (val as any)?.caption || '' });
                }}
                slotProps={{
                  paper: {
                    sx: {
                      width: '100%',
                      maxHeight: 480,
                    }
                  },
                  popper: {
                    sx: {
                      width: '100%'
                    }
                  }
                }}
                sx={{
                  // Span full row on desktop
                  gridColumn: { xs: 'auto', sm: '1 / -1' },
                  minWidth: { xs: 300, sm: '100%' },
                  width: '100%'
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.id}
                    style={{ display: 'flex', alignItems: 'center', padding: 10, minHeight: 64 }}
                  >
                    <Avatar src={option.media_url}
                      sx={{ width: 48, height: 48, mr: 2, borderRadius: 1 }}
                      variant="rounded"
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <Typography variant="body1" noWrap sx={{ fontWeight: 600 }}>
                        {option.caption || option.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                        {option.id} {option.timestamp ? `• ${new Date(option.timestamp as any).toLocaleDateString()}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Media"
                    margin="dense"
                    sx={{ mt: 1 }}
                  />
                )}
              />
              <TextField label="Keyword" fullWidth size="small" margin="dense" sx={{ mt: 1 }} value={newRule.keyword} onChange={(e) => setNewRule({ ...newRule, keyword: e.target.value })} />
              <TextField label="Auto DM Reply" fullWidth size="small" margin="dense" sx={{ mt: 1 }} value={newRule.replyText} onChange={(e) => setNewRule({ ...newRule, replyText: e.target.value })} />
            </Box>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={saveRule}>Add Rule</Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Media</TableCell>
                  <TableCell>Keyword</TableCell>
                  <TableCell>Reply</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {rules.map((r) => (
                    <TableRow key={r._id}>
                      <TableCell sx={{ maxWidth: { xs: 260, sm: 420 }, whiteSpace: 'nowrap' }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={r.mediaTitle || r.mediaId}
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                        >
                          {r.mediaTitle || r.mediaId}
                        </Typography>
                      </TableCell>
                    <TableCell>{r.keyword}</TableCell>
                    <TableCell>{r.replyText}</TableCell>
                    <TableCell>{r.isActive ? <Chip label="Active" color="success" size="small"/> : <Chip label="Inactive" size="small"/>}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => toggleRule(r._id, r.isActive)}>{r.isActive ? 'Disable' : 'Enable'}</Button>
                      <Button size="small" color="error" onClick={() => deleteRule(r._id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRules}>Close</Button>
        </DialogActions>
      </Dialog>
      </Box>

      {noConnectedAccounts && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1300,
          pointerEvents: 'auto',
          userSelect: 'auto',
          width: '100%',
          maxWidth: 520
        }}>
          <Alert severity="warning" sx={{
            backgroundColor: '#FFF7E6',
            border: '1px solid #FFE1A3',
            color: '#8A6D3B',
            width: '100%',
            textAlign: 'center',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              No Connected Accounts
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Connect at least one social media account to view and manage your comments.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              onClick={() => window.dispatchEvent(new Event('open-connect-dropdown'))}
              sx={{ fontWeight: 600 }}
            >
              Connect Account
            </Button>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Comments;


